import { USER_TYPE } from 'app/config/users';
import { PAD_VISIT_TYPE } from 'app/config/padVisits';
import {
  VISIT_SCHEDULE_KEYS,
  NORMALIZED_KEYS
} from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/visitScheduleStep.reducer';
import { normalizeVisitPrescriptionSchedule } from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/utils/normalizeVisitPrescriptionSchedule';
import { transformPrescriptionScheduleToLoadSchedule } from './utils/transformPrescriptionScheduleToLoadSchedule.js';
import { shouldIncludeAsCalendarVisitSchedule } from './utils/shouldIncludeAsCalendarVisitSchedule.js';
import { transferRulesRegistry } from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/utils/transferRules.js';

/**
 * Applies all registered transfer rules in priority order.
 *
 * @param {Array} prescriptionSchedules - Initial prescription schedules
 * @param {Array} validPrescriptions - Prescriptions array
 * @returns {{ schedulesForCalendar: Array, schedulesForPrescription: Array }}
 */
function applyTransferRules(prescriptionSchedules, validPrescriptions) {
  return transferRulesRegistry
    .slice()
    .sort((a, b) => a.priority - b.priority)
    .reduce((acc, { fn }) => fn(acc.schedulesForPrescription, validPrescriptions), {
      schedulesForCalendar: [],
      schedulesForPrescription: prescriptionSchedules
    });
}

const isSuspensionVisit = padVisit => {
  const { padVisitType } = padVisit;
  const isSuspension = padVisitType === PAD_VISIT_TYPE.SUSPENSION;

  return isSuspension;
};

const isCooperative = padVisit => {
  const { responsibleId, responsibleType } = padVisit;
  return responsibleType === USER_TYPE.COOPERATIVE || responsibleId;
};

const hasLinkWithPadPrescription = padVisit => {
  const { padPrescriptions = [] } = padVisit;
  return Array.isArray(padPrescriptions) && padPrescriptions.length > 0;
};

export const shouldIncludeAsCalendarVisitScheduleCooperative = padVisit => {
  return (
    isCooperative(padVisit) && !isSuspensionVisit(padVisit) && !hasLinkWithPadPrescription(padVisit)
  );
};

export const shouldIncludeAsSuspensionVisits = padVisit => {
  const { padVisitType } = padVisit;

  const isPadVisitSuspension = padVisitType === PAD_VISIT_TYPE.SUSPENSION;

  return isPadVisitSuspension;
};

/**
 * Creates a new calendar state with filtered entries by removing specified padVisitIds.
 * @param {Object} state - Current state
 * @param {string[]} padVisitIdsToRemove - Array of padVisitIds to remove
 * @returns {Object} New calendar state with filtered entries
 */
// Tornar genérico para qualquer key (mantendo retrocompatibilidade)
const createFilteredScheduleState = (state, padVisitIdsToRemove, scheduleKey) => {
  const scheduleState = state[scheduleKey];

  const byPadVisitId = scheduleState[NORMALIZED_KEYS.BY_PAD_VISIT_ID] || {};
  const padVisitIds = scheduleState[NORMALIZED_KEYS.IDS] || [];

  const filteredByPadVisitId = padVisitIds
    .filter(padVisitId => !padVisitIdsToRemove.includes(padVisitId))
    .reduce((result, padVisitId) => {
      result[padVisitId] = byPadVisitId[padVisitId];
      return result;
    }, {});

  return {
    ...scheduleState,
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: filteredByPadVisitId,
    [NORMALIZED_KEYS.IDS]: padVisitIds.filter(
      padVisitId => !padVisitIdsToRemove.includes(padVisitId)
    )
  };
};

/**
 * Handles missing padVisits that need to be added to either calendar or daily schedules.
 * @param {Object} state - Current state
 * @param {Object} newCalendarState - Filtered calendar state
 * @param {Object} newDailyState - Filtered daily state
 * @param {Array} schedulesToAdd - Schedules that need to be added
 * @returns {Object} Object with newCalendarVisitSchedule and newVisitDailySchedule
 */
const handleMissingPadVisits = (
  state,
  newCalendarState,
  newDailyState,
  schedulesToAdd,
  shouldIncludeAsCalendarFn
) => {
  let mergedCalendarIds = [...(newCalendarState[NORMALIZED_KEYS.IDS] || [])];
  let mergedCalendarByPadVisitId = { ...(newCalendarState[NORMALIZED_KEYS.BY_PAD_VISIT_ID] || {}) };

  let mergedDailyIds = [...(newDailyState[NORMALIZED_KEYS.IDS] || [])];
  let mergedDailyByPadVisitId = { ...(newDailyState[NORMALIZED_KEYS.BY_PAD_VISIT_ID] || {}) };

  schedulesToAdd.forEach(schedule => {
    const { padVisit } = schedule;
    const padVisitId = padVisit.id;

    const shouldIncludeAsCalendar = shouldIncludeAsCalendarFn(padVisit);

    if (shouldIncludeAsCalendar) {
      if (!mergedCalendarIds.includes(padVisitId) && !newCalendarState.byPadVisitId[padVisitId]) {
        mergedCalendarIds.push(padVisitId);
        mergedCalendarByPadVisitId[padVisitId] = schedule;
      }
    } else {
      if (!mergedDailyIds.includes(padVisitId) && !newDailyState.byPadVisitId[padVisitId]) {
        mergedDailyIds.push(padVisitId);
        mergedDailyByPadVisitId[padVisitId] = schedule;
      }
    }
  });

  return {
    newCalendarVisitSchedule: {
      ...newCalendarState,
      [NORMALIZED_KEYS.IDS]: mergedCalendarIds,
      [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: mergedCalendarByPadVisitId
    },
    newVisitDailySchedule: {
      ...newDailyState,
      [NORMALIZED_KEYS.IDS]: mergedDailyIds,
      [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: mergedDailyByPadVisitId
    }
  };
};

/**
 * Creates a new event with updated padPrescriptionIds array.
 * @param {Object} event - The event object
 * @param {number} prescriptionId - The prescription ID to add/remove
 * @param {boolean} shouldAdd - true to add, false to remove
 * @returns {Object} New event with updated padPrescriptionIds
 */
const updateEventPadPrescriptionIds = (event, prescriptionId, shouldAdd) => {
  if (!event) return event;

  const currentIds = Array.isArray(event.padPrescriptionIds) ? event.padPrescriptionIds : [];

  let newIds;
  if (shouldAdd) {
    // Add only if not already present
    newIds = currentIds.includes(prescriptionId) ? currentIds : [...currentIds, prescriptionId];
  } else {
    // Remove if present
    newIds = currentIds.filter(id => id !== prescriptionId);
  }

  return {
    ...event,
    padPrescriptionIds: newIds
  };
};

/**
 * Updates all events in a schedule based on padVisitId comparison.
 * @param {Object} schedule - The schedule object
 * @param {number} prescriptionId - The prescription ID
 * @param {number|string|null} sourcePadVisitId - The padVisitId from the prescription
 * @returns {Object} New schedule with updated events
 */
const updateScheduleEvents = (schedule, prescriptionId, sourcePadVisitId) => {
  if (!schedule || !Array.isArray(schedule.events)) {
    return schedule;
  }

  const schedulePadVisitId = schedule.padVisitId;
  const hasMatchingPadVisitId =
    sourcePadVisitId !== null &&
    sourcePadVisitId !== undefined &&
    schedulePadVisitId === sourcePadVisitId;

  const shouldAdd = hasMatchingPadVisitId;

  const updatedEvents = schedule.events.map(event =>
    updateEventPadPrescriptionIds(event, prescriptionId, shouldAdd)
  );

  return {
    ...schedule,
    events: updatedEvents
  };
};

/**
 * Updates padPrescriptionIds in events across all three collections
 * (calendar, daily, prescription) based on the prescriptions array.
 * @param {Object} state - State after schedule movement
 * @param {Array} schedulePrescriptions - Array of valid prescriptions with id and padVisitId
 * @returns {Object} State with updated event padPrescriptionIds
 */
const updateAllCollectionsPadPrescriptionIds = (state, schedulePrescriptions) => {
  // Helper to update a single collection's events
  const updateCollectionEvents = collection => {
    if (!collection || !collection.byPadVisitId) {
      return collection;
    }

    const updatedByPadVisitId = { ...collection.byPadVisitId };

    Object.keys(updatedByPadVisitId).forEach(padVisitId => {
      const schedule = updatedByPadVisitId[padVisitId];
      let updatedSchedule = { ...schedule };

      // Process each prescription to determine if it should be added/removed
      schedulePrescriptions.forEach(prescription => {
        const { id: prescriptionId, padVisitId: sourcePadVisitId } = prescription;

        if (prescriptionId === undefined || prescriptionId === null) {
          return;
        }

        updatedSchedule = updateScheduleEvents(updatedSchedule, prescriptionId, sourcePadVisitId);
      });

      updatedByPadVisitId[padVisitId] = updatedSchedule;
    });

    return {
      ...collection,
      byPadVisitId: updatedByPadVisitId
    };
  };

  return {
    ...state,
    [VISIT_SCHEDULE_KEYS.CALENDAR]: updateCollectionEvents(state.calendarVisitSchedule),
    [VISIT_SCHEDULE_KEYS.DAILY]: updateCollectionEvents(state.calendarDailySchedules),
    [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: updateCollectionEvents(state.visitPrescriptionSchedules)
  };
};

/**
 * Função genérica para intercâmbio entre dois schedules no state redux.
 */
export function exchangeSchedules(
  state,
  schedulePrescriptions,
  { calendarKey, prescriptionKey, dailyKey = VISIT_SCHEDULE_KEYS.DAILY }
) {
  if (!Array.isArray(schedulePrescriptions) || schedulePrescriptions.length === 0) {
    return getEmptyResult({ state, calendarKey, dailyKey, prescriptionKey });
  }

  const validPrescriptions = schedulePrescriptions.filter(item => item && item.id !== null);

  if (validPrescriptions.length === 0) {
    return getUnchangedResult(state, calendarKey, dailyKey, prescriptionKey);
  }

  // 1. Consolidate all schedules from three collections
  const allVisitSchedules = consolidateVisitSchedules({
    state,
    calendarKey,
    dailyKey,
    prescriptionKey
  });

  // 2. padVisitIds do PAD corrente (pad que está sendo inicializado) que estão
  // vinculados com alguma prescrição, esses serão transferidos para "visitPrescriptionSchedule"
  const padVisitIdsToTransfer = getPadVisitIdsToTransfer(schedulePrescriptions, allVisitSchedules);

  // 3. Build prescription schedules WITH prescriptionLoadSchedule
  const schedulesForPrescription = buildPrescriptionSchedules({
    allVisitSchedules,
    padVisitIdsToTransfer,
    schedulePrescriptions
  });

  // 4. Apply all registered transfer rules (split rule, future rules, etc.)
  const { schedulesForCalendar, schedulesForPrescription: finalPrescriptionSchedules } =
    applyTransferRules(schedulesForPrescription, validPrescriptions);

  // 5. Normalize prescription schedules
  const newVisitPrescriptionSchedule = normalizeVisitPrescriptionSchedule(
    finalPrescriptionSchedules
  );

  // 6. Remove transferred padVisitIds from calendar/daily
  const filteredCalendar = createFilteredScheduleState(state, padVisitIdsToTransfer, calendarKey);
  const filteredDaily = createFilteredScheduleState(state, padVisitIdsToTransfer, dailyKey);

  const isCooperative = calendarKey === VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE;
  const shouldIncludeFn = isCooperative
    ? shouldIncludeAsCalendarVisitScheduleCooperative
    : shouldIncludeAsCalendarVisitSchedule;

  // 7. Handle missing padVisits (re-add non-transferred schedules + split remnants)
  const { newCalendarVisitSchedule, newVisitDailySchedule } = handleMissingPadVisits(
    state,
    filteredCalendar,
    filteredDaily,
    schedulesForCalendar,
    shouldIncludeFn
  );

  const intermediateState = {
    ...state,
    [calendarKey]: newCalendarVisitSchedule,
    [dailyKey]: newVisitDailySchedule,
    [prescriptionKey]: newVisitPrescriptionSchedule
  };

  // 8. Update padPrescriptionIds across all collections
  return updateAllCollectionsPadPrescriptionIds(intermediateState, validPrescriptions, {
    calendarKey,
    dailyKey,
    prescriptionKey
  });
}

/**
 * Returns empty result for empty prescriptions array.
 */
function getEmptyResult({ state, calendarKey, dailyKey, prescriptionKey }) {
  return {
    [calendarKey]: state[calendarKey],
    [dailyKey]: state[dailyKey],
    [prescriptionKey]: {
      ids: [],
      byPadVisitId: {}
    }
  };
}

/**
 * Returns unchanged result for invalid prescriptions.
 */
function getUnchangedResult(state, calendarKey, dailyKey, prescriptionKey) {
  return {
    [calendarKey]: state[calendarKey],
    [dailyKey]: state[dailyKey],
    [prescriptionKey]: state[prescriptionKey]
  };
}

/**
 * Consolidates schedules from all three collections into a flat array.
 */
function consolidateVisitSchedules({ state, calendarKey, dailyKey, prescriptionKey }) {
  return [
    ...Object.values(state[calendarKey].byPadVisitId || {}),
    ...Object.values(state[dailyKey].byPadVisitId || {}),
    ...Object.values(state[prescriptionKey].byPadVisitId || {})
  ]
    .filter(Boolean)
    .map(schedule => ({ ...schedule, events: [], prescriptionLoadSchedule: [] }));
}

/**
 * Extracts padVisitIds from PAD Prescriptions that are not inherited and are
 * not in this current PAD that is being initialize.
 */
function getPadVisitIdsToTransfer(schedulePrescriptions, allVisitSchedules) {
  return schedulePrescriptions
    .filter(schedulePrescription => {
      const { padVisitId, cPadVisitId, isPADVisitInherited } = schedulePrescription;

      // padVisitIds do PAD corrente (que está sendo inicializado)
      const currentPadVisitIds = allVisitSchedules.map(schedule => schedule.id);

      const isPadVisitFromCurrentPAD = currentPadVisitIds.includes(padVisitId);

      if (!isPadVisitFromCurrentPAD) {
        // significa que o padVisitId atribuído não pertence ao pad corrente
        // (que está sendo inicializado), portanto ele não é intercambiável
        // entre uma agenda e outra pois já foi agendado no PAD anterior.
        return false;
      }

      if (isPADVisitInherited) {
        // significa que embora o item de prescrição tenha um vínculo pretérito,
        // o usuário atribuiu outra visita
        return padVisitId !== cPadVisitId;
      }

      return !!padVisitId;
    })
    .map(({ padVisitId }) => padVisitId);
}

/**
 * Builds prescription schedules with computed prescriptionLoadSchedule.
 */
function buildPrescriptionSchedules({
  allVisitSchedules,
  padVisitIdsToTransfer,
  schedulePrescriptions
}) {
  return allVisitSchedules
    .filter(schedule => padVisitIdsToTransfer.includes(schedule.padVisitId))
    .map(schedule => {
      const padVisit = schedule.padVisit;
      const relevantPrescriptions = schedulePrescriptions.filter(p => p.padVisitId === padVisit.id);

      const prescriptionLoadSchedule = transformPrescriptionScheduleToLoadSchedule(
        relevantPrescriptions,
        padVisit
      );

      return {
        ...schedule,
        prescriptionLoadSchedule
      };
    });
}

export const computeUpdateSchedulePrescriptionState = (state, schedulePrescriptions) =>
  exchangeSchedules(state, schedulePrescriptions, {
    calendarKey: VISIT_SCHEDULE_KEYS.CALENDAR,
    prescriptionKey: VISIT_SCHEDULE_KEYS.PRESCRIPTION,
    dailyKey: VISIT_SCHEDULE_KEYS.DAILY
  });

export const computeUpdateSchedulePrescriptionCooperativeState = (state, schedulePrescriptions) =>
  exchangeSchedules(state, schedulePrescriptions, {
    calendarKey: VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE,
    prescriptionKey: VISIT_SCHEDULE_KEYS.PRESCRIPTION_COOPERATIVE,
    dailyKey: VISIT_SCHEDULE_KEYS.DAILY
  });
